import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Container, Paper, Grid, CircularProgress, Alert } from '@mui/material';
import { 
  People as PeopleIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { apiEndpoints } from '../config';
import { getAccessToken } from '../services/authService';

interface StatCard {
  title: string;
  value: string;
  icon: React.ReactElement;
  color: string;
}

interface SystemHealthData {
  customers: {
    TotalCustomers: number;
    ActiveCustomers: number;
  };
  deployments: {
    TotalDeployments: number;
    CompletedDeployments: number;
    InProgressDeployments: number;
  };
  policies: {
    TotalPolicies: number;
    DeployedPolicies: number;
  };
  healthScore: number;
  healthStatus: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<StatCard[]>([
    {
      title: 'Total Customers',
      value: '0',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#3f51b5'
    },
    {
      title: 'Active Deployments',
      value: '0',
      icon: <CloudIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50'
    },
    {
      title: 'Policies Managed',
      value: '0',
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      color: '#ff9800'
    },
    {
      title: 'System Health',
      value: '100%',
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      color: '#2196f3'
    }
  ]);

  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const fetchSystemHealth = async () => {
    try {
      setLoading(true);
      setError(null);


      // Use MSAL to get the access token
      const token = await getAccessToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await fetch(apiEndpoints.adminSupport.systemHealth, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch system health: ${response.status} ${errorText}`);
      }

      const data: SystemHealthData = await response.json();

      // Update stats with real data
      setStats([
        {
          title: 'Total Customers',
          value: (data.customers?.TotalCustomers ?? 0).toString(),
          icon: <PeopleIcon sx={{ fontSize: 40 }} />,
          color: '#3f51b5'
        },
        {
          title: 'Active Deployments',
          value: (data.deployments?.InProgressDeployments ?? 0).toString(),
          icon: <CloudIcon sx={{ fontSize: 40 }} />,
          color: '#4caf50'
        },
        {
          title: 'Policies Managed',
          value: (data.policies?.TotalPolicies ?? 0).toString(),
          icon: <SecurityIcon sx={{ fontSize: 40 }} />,
          color: '#ff9800'
        },
        {
          title: 'System Health',
          value: `${data.healthScore ?? 100}%`,
          icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
          color: (data.healthScore ?? 100) >= 90 ? '#4caf50' : (data.healthScore ?? 100) >= 70 ? '#ff9800' : '#f44336'
        }
      ]);
    } catch (err) {
      console.error('Error fetching system health:', err);
      setError(err instanceof Error ? err.message : 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Welcome, {adminUser.fullName || 'Admin'}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Role: {adminUser.role || 'Unknown'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mt: 2 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              elevation={3}
              onClick={() => index === 0 && navigate('/customers')}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                borderTop: 4,
                borderColor: stat.color,
                cursor: index === 0 ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': index === 0 ? {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)'
                } : {}
              }}
            >
              <Box sx={{ color: stat.color, mb: 2 }}>
                {stat.icon}
              </Box>
              <Typography variant="h4" gutterBottom>
                {stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.title}
              </Typography>
              {index === 0 && (
                <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
                  Click to view customers page
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
        </Grid>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Dashboard functionality coming soon...
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
}
